import React from "react";
import TopoTexture from "@/components/TopoTexture";
import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, Mail, Star, Facebook } from "lucide-react";
import { COMPANY, SERVICES } from "@/data/site";

const Footer = () => {
  return (
    <footer data-testid="site-footer" className="relative bg-[var(--ct-primary)] text-white mt-24 overflow-hidden">
      <TopoTexture variant="dark" opacity={0.03} />
      <div className="section-container py-16 relative">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img
                src="https://customer-assets.emergentagent.com/job_premium-moving-wy/artifacts/63dddnb5_IMG_0277.webp"
                alt="Crosstown Movers Logo"
                className="w-12 h-12 rounded-lg object-contain bg-black"
              />
              <div className="font-bold text-lg" style={{ fontFamily: "Outfit" }}>Crosstown Movers</div>
            </div>
            <p className="text-white/70 text-sm leading-relaxed">
              Wyoming's trusted local moving company since 1980. Proudly serving Casper, Mills, and surrounding areas with residential, commercial, and white-glove moving services.
            </p>
            <div className="flex items-center gap-1 mt-4">
              {[1,2,3,4,5].map((i) => (
                <Star key={i} className={`w-4 h-4 ${i <= 4 ? 'fill-[var(--ct-secondary)] text-[var(--ct-secondary)]' : 'text-white/30'}`} />
              ))}
              <span className="ml-2 text-sm text-white/80">{COMPANY.rating} · {COMPANY.reviews} reviews</span>
            </div>
          </div>

          {/* Services */}
          <div>
            <div className="label-xs text-white/60 mb-4">Services</div>
            <ul className="space-y-2">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    className="text-sm text-white/80 hover:text-[var(--ct-secondary)] transition"
                    data-testid={`footer-service-${s.slug}`}
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Service Areas */}
          <div>
            <div className="label-xs text-white/60 mb-4">Service Areas</div>
            <ul className="space-y-2">
              {COMPANY.serviceAreas.map((a) => (
                <li key={a} className="text-sm text-white/80">{a}</li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <div className="label-xs text-white/60 mb-4">Contact</div>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-[var(--ct-secondary)] flex-shrink-0" />
                <span className="text-white/80">{COMPANY.address}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-[var(--ct-secondary)]" />
                <a href={`tel:${COMPANY.phoneTel}`} className="text-white/90 hover:text-white font-semibold" data-testid="footer-phone">
                  {COMPANY.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[var(--ct-secondary)]" />
                <a href={`mailto:${COMPANY.email}`} className="text-white/80 hover:text-white">
                  {COMPANY.email}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-[var(--ct-secondary)]" />
                <span className="text-white/80">{COMPANY.hours}</span>
              </li>
              <li className="flex items-center gap-3">
                <Facebook className="w-4 h-4 text-[var(--ct-secondary)]" />
                <a
                  href={COMPANY.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow Crosstown Movers on Facebook"
                  data-testid="footer-facebook"
                  className="inline-flex items-center py-2 text-white/80 hover:text-white rounded focus-visible:outline-2 focus-visible:outline-[var(--ct-secondary)]"
                >
                  Follow us on Facebook
                </a>
              </li>
              <li className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-[var(--ct-secondary)]" />
                <a
                  href={COMPANY.googleMaps}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Find Crosstown Movers on Google Maps"
                  data-testid="footer-google"
                  className="inline-flex items-center py-2 text-white/80 hover:text-white rounded focus-visible:outline-2 focus-visible:outline-[var(--ct-secondary)]"
                >
                  Find us on Google
                </a>
              </li>
            </ul>
            <Link to="/#quote" className="btn-primary mt-5 text-sm" data-testid="footer-cta">
              Get Free Quote
            </Link>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 text-xs text-white/60">
          <div>© {new Date().getFullYear()} {COMPANY.name}. All rights reserved. {COMPANY.website}</div>
          <div className="flex gap-6 flex-wrap">
            <Link to="/service-areas" className="hover:text-white">Service Areas</Link>
            <Link to="/blog" className="hover:text-white">Blog</Link>
            <Link to="/moving-checklist" className="hover:text-white">Moving Checklist</Link>
            <Link to="/casper-moving-resources" className="hover:text-white">Local Resources</Link>
            <Link to="/faq" className="hover:text-white">FAQ</Link>
            <Link to="/about" className="hover:text-white">About</Link>
            <Link to="/sms-terms" className="hover:text-white">SMS Terms</Link>
            <Link to="/privacy-policy" className="hover:text-white">Privacy Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
